"use client";
import React, { useEffect, useState } from "react";
import "@/src/components/Home/LandingV6/landing-v6.css";
import { Nav, Mascot, StartWritingButton, QuarklePlanet } from "@/src/components/Home/LandingV6/ui";
import { Footer } from "@/src/components/Home/LandingV6/blocks";
import { Sparkle, Asterism, CornerPin, TapeSticker, Squiggle } from "@/src/components/Home/LandingV6/ornaments";

const TRACKS = [
  {
    n: "01",
    title: "characters",
    body: "names, ages, relationships, physical details, the line where you first described them.",
    accent: "var(--pink)",
  },
  {
    n: "02",
    title: "chapter events",
    body: "chapter summaries and the story events Quarkle can reference when you ask about an earlier scene.",
    accent: "var(--moss)",
  },
  {
    n: "03",
    title: "pinned canon",
    body: "freeze the facts and rules that should stay fixed, even while the rest of the draft keeps moving.",
    accent: "var(--clay)",
  },
  {
    n: "04",
    title: "revision history",
    body: "see who changed an Atlas note, inspect the word-level diff, and return to a saved version.",
    accent: "var(--violet)",
  },
];

function MemoryDemo() {
  const [memoryOn, setMemoryOn] = useState(true);

  return (
    <div
      style={{
        maxWidth: 780,
        margin: "0 auto",
        background: "var(--paper-3)",
        border: "1.5px solid var(--ink)",
        borderRadius: 12,
        boxShadow: "12px 12px 0 var(--ink)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 20px",
          borderBottom: "1.5px solid var(--ink)",
          background: "var(--paper-2)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 10, height: 10, borderRadius: 999, background: "#e0605a", display: "inline-block" }} />
          <span style={{ width: 10, height: 10, borderRadius: 999, background: "#e0c05a", display: "inline-block" }} />
          <span style={{ width: 10, height: 10, borderRadius: 999, background: "#6bb06b", display: "inline-block" }} />
          <span className="mono" style={{ fontSize: 11, opacity: 0.6, marginLeft: 6 }}>chat · the hollow house.md</span>
        </div>
        <button
          onClick={() => setMemoryOn((v) => !v)}
          className="mono"
          style={{
            all: "unset",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 12px",
            borderRadius: 999,
            border: "1.5px solid var(--ink)",
            background: memoryOn ? "var(--moss)" : "var(--paper)",
            color: memoryOn ? "#fff" : "var(--ink)",
            fontSize: 10,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            transition: "all 0.2s ease",
          }}
        >
          <span
            aria-hidden
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: memoryOn ? "var(--lime)" : "var(--pink)",
              display: "inline-block",
            }}
          />
          memory: {memoryOn ? "on" : "off"}
        </button>
      </div>

      <div style={{ padding: "22px 24px", display: "grid", gap: 16 }}>
        <div className="mono" style={{ fontSize: 10, opacity: 0.5, textAlign: "center", letterSpacing: "0.14em", textTransform: "uppercase" }}>
          chapter 03 · 1,900 words in
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <p
            className="display"
            style={{
              margin: 0,
              maxWidth: "80%",
              fontSize: 16,
              fontStyle: "italic",
              lineHeight: 1.5,
              background: "var(--paper)",
              border: "1px solid var(--ink)",
              borderRadius: "12px 12px 2px 12px",
              padding: "12px 16px",
            }}
          >
            Eldra pulled her hood back. Her eyes were the grey of a winter storm, the same grey her mother's had been.
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0" }}>
          <div style={{ flex: 1, borderTop: "1px dashed var(--rule)" }} />
          <span className="mono" style={{ fontSize: 10, opacity: 0.5, letterSpacing: "0.1em", whiteSpace: "nowrap" }}>
            · · · 37 chapters and 40 chat messages later · · ·
          </span>
          <div style={{ flex: 1, borderTop: "1px dashed var(--rule)" }} />
        </div>

        <div className="mono" style={{ fontSize: 10, opacity: 0.5, textAlign: "center", letterSpacing: "0.14em", textTransform: "uppercase" }}>
          chapter 40 · you, in chat
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <p
            className="display"
            style={{
              margin: 0,
              maxWidth: "80%",
              fontSize: 16,
              fontStyle: "italic",
              lineHeight: 1.5,
              background: "var(--paper)",
              border: "1px solid var(--ink)",
              borderRadius: "12px 12px 2px 12px",
              padding: "12px 16px",
            }}
          >
            quick continuity check: what color are Eldra's eyes again? don't want to contradict ch.3.
          </p>
        </div>

        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <Mascot pose={memoryOn ? "teaching" : "thinking"} size={40} float={false} />
          <div
            style={{
              flex: 1,
              background: memoryOn ? "color-mix(in oklab, var(--moss) 10%, var(--paper))" : "color-mix(in oklab, var(--pink) 8%, var(--paper))",
              border: `1.5px solid ${memoryOn ? "var(--moss)" : "var(--pink)"}`,
              borderRadius: "2px 12px 12px 12px",
              padding: "14px 18px",
              transition: "all 0.2s ease",
            }}
          >
            {memoryOn ? (
              <>
                <p className="display" style={{ margin: 0, fontSize: 16, fontStyle: "italic", lineHeight: 1.5 }}>
                  grey: the grey of a winter storm, same as her mother's. you set that in chapter 3, so anything else here would contradict it.
                </p>
                <span
                  className="mono"
                  style={{
                    display: "inline-block",
                    marginTop: 10,
                    fontSize: 10,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "var(--moss)",
                    border: "1px solid var(--moss)",
                    borderRadius: 999,
                    padding: "2px 10px",
                  }}
                >
                  ✓ ch. 03 · ln 42
                </span>
              </>
            ) : (
              <p className="display" style={{ margin: 0, fontSize: 16, fontStyle: "italic", lineHeight: 1.5, opacity: 0.85 }}>
                I don't have chapter 3 open right now, so I can't check. you'll want to re-paste that passage, or I'll have to guess. blue, maybe?
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MemoryPage() {
  const [theme, setTheme] = useState("paper");
  useEffect(() => {
    try {
      const s = window.localStorage.getItem("qv6-theme");
      if (s === "paper" || s === "midnight") setTheme(s);
    } catch (_) {}
  }, []);

  return (
    <div className={`qv6 ${theme === "midnight" ? "theme-midnight" : ""}`}>
      <Nav />

      <section style={{ padding: "80px 40px 40px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div aria-hidden style={{ position: "absolute", top: 60, right: 80 }}>
          <Sparkle size={34} color="var(--pink)" style={{ animation: "qv6-sparkle 3.2s infinite" }} />
        </div>
        <div aria-hidden style={{ position: "absolute", top: 140, left: 70 }}>
          <Asterism size={26} color="var(--clay)" />
        </div>
        <div style={{ position: "relative", maxWidth: 880, margin: "0 auto" }}>
          <CornerPin color="var(--pink)">feature deep-dive · memory</CornerPin>
          <h1
            className="tanker"
            style={{
              fontSize: "clamp(52px, 8vw, 128px)",
              lineHeight: 0.9,
              letterSpacing: "-0.035em",
              margin: "16px 0 18px",
            }}
          >
            it read chapter 3.
            <br />
            it still <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>remembers</span> chapter 3.
          </h1>
          <p className="display" style={{ fontSize: 20, fontStyle: "italic", opacity: 0.82, maxWidth: 640, margin: "0 auto" }}>
            Quarkle turns the manuscript into a living set of story notes, then keeps those notes current as the draft changes. chat and review passes can work from the book without asking you to rebuild the context every time.
          </p>
        </div>
      </section>

      <section style={{ padding: "20px 40px 100px" }}>
        <MemoryDemo />
        <p className="mono" style={{ textAlign: "center", fontSize: 11, opacity: 0.5, marginTop: 18, letterSpacing: "0.08em" }}>
          try the toggle above: same question, memory on vs. off.
        </p>
      </section>

      <section
        style={{
          padding: "90px 40px 100px",
          background: "var(--paper-2)",
          borderTop: "1.5px solid var(--ink)",
          borderBottom: "1.5px solid var(--ink)",
        }}
      >
        <div style={{ maxWidth: 1160, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <span className="caps" style={{ color: "var(--pink)" }}>what quarkle keeps track of</span>
            <h2 className="tanker" style={{ fontSize: "clamp(40px, 5vw, 76px)", margin: "10px 0 0", lineHeight: 0.96 }}>
          four parts of the draft it keeps <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>within reach</span>.
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 22 }}>
            {TRACKS.map((t) => (
              <article
                key={t.n}
                style={{
                  background: "var(--paper-3)",
                  border: "1.5px solid var(--ink)",
                  borderRadius: 12,
                  padding: "24px 22px",
                  boxShadow: "6px 6px 0 var(--ink)",
                }}
              >
                <span className="tanker" style={{ fontSize: 56, fontStyle: "italic", color: t.accent, lineHeight: 0.85 }}>
                  {t.n}
                </span>
                <h3 className="tanker" style={{ fontSize: 24, margin: "10px 0 0" }}>{t.title}</h3>
                <Squiggle w={100} h={10} color={t.accent} />
                <p className="display" style={{ fontSize: 15, lineHeight: 1.5, fontStyle: "italic", opacity: 0.85, marginTop: 10 }}>
                  {t.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: "90px 40px", textAlign: "center" }}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <span className="caps" style={{ color: "var(--pink)" }}>how this compares</span>
          <h2 className="tanker" style={{ fontSize: "clamp(36px, 4.4vw, 64px)", margin: "10px 0 16px", lineHeight: 1 }}>
            story-memory tools make <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>different tradeoffs</span>.
          </h2>
          <p className="display" style={{ fontSize: 18, fontStyle: "italic", opacity: 0.82, marginBottom: 28 }}>
            some prioritize a deeply configurable story bible; others rely mainly on a model's context window. Quarkle prioritizes manuscript-derived notes that feed directly into editorial review and chat.
          </p>
          <div style={{ display: "inline-flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
            <TapeSticker color="var(--lime)" rotate={-2}>
              <a href="/vs/sudowrite" style={{ textDecoration: "none", color: "inherit" }}>vs. sudowrite →</a>
            </TapeSticker>
            <TapeSticker color="var(--sky)" rotate={1.5}>
              <a href="/vs/novelcrafter" style={{ textDecoration: "none", color: "inherit" }}>vs. novelcrafter →</a>
            </TapeSticker>
            <TapeSticker color="var(--oat)" rotate={-1}>
              <a href="/vs/scrivener" style={{ textDecoration: "none", color: "inherit" }}>vs. scrivener →</a>
            </TapeSticker>
            <TapeSticker color="var(--clay)" rotate={2}>
              <a href="/vs/novelai" style={{ textDecoration: "none", color: "inherit" }}>vs. novelai →</a>
            </TapeSticker>
          </div>
        </div>
      </section>

      <section style={{ padding: "80px 40px 100px", background: "var(--navy)", color: "var(--paper-3)", textAlign: "center" }}>
        <QuarklePlanet size={44} color="var(--pink)" ring="var(--paper-3)" />
        <h2 className="tanker" style={{ fontSize: "clamp(40px, 5vw, 76px)", margin: "16px 0 20px", lineHeight: 0.95 }}>
          bring the book. <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>we'll remember it.</span>
        </h2>
        <StartWritingButton variant="pink" size="xl">start writing →</StartWritingButton>
      </section>

      <Footer />
    </div>
  );
}
